import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon } from "@phosphor-icons/react";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";

export type DateRangeOption = "30d" | "60d" | "90d" | "mtd" | "ytd";

interface DateRangeSelectorProps {
  value: DateRangeOption;
  onChange: (value: DateRangeOption) => void;
}

export function DateRangeSelector({ value, onChange }: DateRangeSelectorProps) {
  const { t } = useTranslation("dashboard");
  const { range } = useDateFormat();
  const getDateRangeDisplay = (option: DateRangeOption): string => {
    const now = new Date();
    let from: Date;
    const to = now;

    switch (option) {
      case "30d":
        from = new Date(now);
        from.setDate(from.getDate() - 30);
        break;
      case "60d":
        from = new Date(now);
        from.setDate(from.getDate() - 60);
        break;
      case "90d":
        from = new Date(now);
        from.setDate(from.getDate() - 90);
        break;
      case "mtd":
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "ytd":
        from = new Date(now.getFullYear(), 0, 1);
        break;
    }

    return range(from, to);
  };

  const dateRangeOptions: DateRangeOption[] = [
    "30d",
    "60d",
    "90d",
    "mtd",
    "ytd",
  ];

  return (
    <div className="flex items-center gap-2">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[280px]">
          <SelectValue>
            <div className="flex items-center w-full space-x-2">
              <CalendarIcon
                className="h-5 w-5 text-muted-foreground shrink-0"
                weight="fill"
              />
              <div className="flex items-center justify-between w-full space-x-2">
                <span className="font-medium">
                  {t(`dateRange.${value}` as const)}{" "}
                  <span className="text-xs text-muted-foreground">
                    {getDateRangeDisplay(value)}
                  </span>
                </span>
              </div>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {dateRangeOptions.map((option) => (
            <SelectItem key={option} value={option}>
              <div className="flex flex-col">
                <span>{t(`dateRange.${option}` as const)}</span>
                <span className="text-xs text-muted-foreground">
                  {getDateRangeDisplay(option)}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
